import * as TabsPrimitive from '@radix-ui/react-tabs'
import { Link, LinkProps } from '@tanstack/react-router'
import * as React from 'react'

import styles from './styles.module.css'

import { cn } from '@/utils/cn'

const Tabs = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Root ref={ref} className={cn('mb-4 w-full border-b border-border', className)} {...props} />
))
Tabs.displayName = TabsPrimitive.Root.displayName

export interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
    ({ className, ...props }, ref) => <TabsPrimitive.List ref={ref} className={cn(className)} {...props} />,
)
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
            'inline-flex items-center justify-center whitespace-nowrap border-b-2 border-transparent px-3 py-3 text-sm font-medium transition-all focus-visible:border-primary focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-primary data-[state=active]:text-foreground',
            styles.trigger,
            className,
        )}
        {...props}
    />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

interface TabsTriggerAsLinkProps extends Omit<LinkProps, 'ref'> {
    className?: string
    active?: boolean
    children: React.ReactNode
}

const TabsTriggerAsLink = React.forwardRef<HTMLAnchorElement, TabsTriggerAsLinkProps>(
    ({ className, active, children, ...props }, ref) => (
        <Link
            ref={ref}
            className={cn(
                'inline-flex items-center justify-center whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium ring-offset-background transition-all focus-visible:border-primary focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                styles.trigger,
                {
                    'bg-background': active,
                    'text-foreground': active,
                    'border-primary': active,
                    'border-transparent': !active,
                },
                className,
            )}
            {...(props as any)}
        >
            {children}
        </Link>
    ),
)

TabsTriggerAsLink.displayName = 'TabsTriggerAsLink'

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className,
        )}
        {...props}
    />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsTriggerAsLink }
